/**
 * API Response Types for Kampus Unified Frontend
 * Based on backend endpoint responses
 */

// ============================================
// TTS Pipeline Types
// ============================================

export interface TTSJobMetadata {
  job_id: string;
  pdf_path: string;
  tenant_id: string;
  priority: number;
  voice_id: string;
  status: string;
  created_at: number;
  updated_at: number;
  worker_id: string | null;
  error_message: string | null;
  progress: number;
  chunk_size?: number;
  quality?: string;
  skip_credits?: boolean;
  dry_run?: boolean;
  isbn?: string;
}

export interface TTSJob {
  id: number;
  job_id: string;
  pdf_path: string | null;
  tenant_id: string | null;
  priority: number;
  voice_id: string | null;
  chunk_size: number;
  status: 'pending' | 'running' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  worker_id: string | null;
  error_message: string | null;
  progress: number;
  metadata: string; // JSON string of TTSJobMetadata
  status_message: string;
}

export interface QueueStats {
  pending_jobs: number;
  processing_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  total_workers: number;
  active_workers: number;
}

export interface JobMetrics {
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  success_rate: number;
  average_processing_time: number;
}

export interface CombinedStats {
  queue_stats: QueueStats;
  job_metrics: JobMetrics;
  system_status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  backend_type: string;
}

export interface TelemetryRun {
  id: string;
  run_id: string;
  status: 'completed' | 'processing' | 'pending' | 'failed';
  duration_ms: number | null;
  started_at: string;
  completed_at: string | null;
}

export interface TelemetrySummary {
  event_counts: Record<string, number>;
  extract_structure: TelemetryStage;
  synthesize_tts: TelemetryStage;
  assemble_audio: TelemetryStage;
  generate_credits: TelemetryStage;
  qa_finalize: TelemetryStage;
}

export interface TelemetryStage {
  started: string | null;
  completed: string | null;
  status: 'not_started' | 'in_progress' | 'completed' | 'failed';
}

export interface TelemetryResponse {
  run_id: string;
  total_events: number;
  events: TelemetryEvent[];
  summary: TelemetrySummary;
}

export interface TelemetryEvent {
  id: number;
  job_id: string;
  event_type: string;
  timestamp: string;
  payload: string;
}

// ============================================
// Distribution Types
// ============================================

export interface Book {
  id: number;
  isbn: string;
  title: string;
  publisher_id: number;
  status: 'PENDING' | 'PUBLISHED' | 'FAILED' | 'PROCESSING';
  created_at: string;
  updated_at: string;
}

export interface Publisher {
  id: number;
  name: string;
  code: string;
  created_at: string;
}

export interface Platform {
  id: number;
  name: string;
  code: string;
  status: 'active' | 'inactive';
}

// ============================================
// Health & System Types
// ============================================

export type HealthStatus = 'ok' | 'degraded';

export type HealthCheckValue = 'ok' | 'error' | 'unknown';

export interface HealthChecks {
  api: HealthCheckValue;
  tts_worker: HealthCheckValue;
  distribution_worker: HealthCheckValue;
  db: HealthCheckValue;
}

export interface HealthCheck {
  status: HealthStatus;
  checks: HealthChecks;
  timestamp: string;
}

// ============================================
// API Response Wrappers
// ============================================

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
}

export interface ApiErrorResponse {
  success: false;
  error: string;
  message?: string;
  details?: unknown;
}

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// ============================================
// Helper Functions
// ============================================

/**
 * Parse TTSJob metadata JSON string
 */
export function parseJobMetadata(job: TTSJob): TTSJobMetadata | null {
  try {
    return JSON.parse(job.metadata) as TTSJobMetadata;
  } catch {
    return null;
  }
}

/**
 * Get title from job metadata or job_id
 */
export function getJobTitle(job: TTSJob): string {
  const metadata = parseJobMetadata(job);
  return metadata?.pdf_path?.split('/').pop()?.replace('.pdf', '') || job.job_id;
}

/**
 * Get voice from job metadata
 */
export function getJobVoice(job: TTSJob): string {
  const metadata = parseJobMetadata(job);
  return metadata?.voice_id || 'Unknown';
}

/**
 * Get ISBN from job metadata
 */
export function getJobIsbn(job: TTSJob): string | null {
  const metadata = parseJobMetadata(job);
  return metadata?.isbn || null;
}

/**
 * Format duration from milliseconds
 */
export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
